import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sun, Moon, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLang } from '../../i18n/LanguageContext';
import { PLATFORM_PAGES } from '../../config/platformPages';
import paperBrainzLogo from '../../assets/PaperBrainzLogo.png';
import paperBrainzWhiteLogo from '../../assets/PaperBrainzWhiteLogo.png';

// The single navigation for every public page, including the homepage. The homepage
// used to carry its own inline copy, which drifted from this one — same site, two
// different headers depending on which page you landed on.
//
// Two kinds of destination now coexist:
//   - section anchors, which only exist on the homepage, so they are prefixed with "/"
//     when rendered anywhere else and route home first;
//   - real pages (/pricing, /ai-chatbot-for-*), which are plain links everywhere.
function LandingNav({ isLandingPage = false }) {
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const { theme, toggleTheme } = useTheme();
  const { t } = useLang();
  const d = theme === 'dark';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const integrationsRef = useRef(null);
  const integrationsMenuRef = useRef(null);
  const navRef = useRef(null);
  const hoverCloseTimer = useRef(null);
  // The menu renders outside <nav> (see below), so its position has to be measured
  // rather than inherited from a positioned ancestor.
  const [menuPos, setMenuPos] = useState({ left: 0, top: 0 });
  const location = useLocation();

  const sectionItems = [
    { id: 'how-it-works', label: t('nav.howItWorks') },
    { id: 'features', label: t('nav.features') },
    { id: 'faq', label: t('nav.faq') },
  ];

  const sectionHref = (id) => (isLandingPage ? `#${id}` : `/#${id}`);

  const isCurrent = (path) => location.pathname === path;

  // Highlight the section currently in view, but only on the homepage where the
  // sections actually exist.
  useEffect(() => {
    if (!isLandingPage) {
      setActiveSection('');
      return;
    }
    const elements = sectionItems.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (elements.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const top = visible.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
          setActiveSection(top.target.id);
        }
      },
      { rootMargin: '-96px 0px -60% 0px', threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isLandingPage, location.pathname]);

  // The menu is anchored to the trigger horizontally but hangs from the bottom of the
  // nav pill, not from the trigger — the trigger sits mid-pill, so hanging off it would
  // put the panel on top of the nav.
  const placeMenu = () => {
    const trigger = integrationsRef.current?.getBoundingClientRect();
    const nav = navRef.current?.getBoundingClientRect();
    if (trigger && nav) setMenuPos({ left: trigger.left, top: nav.bottom });
  };

  const openIntegrations = () => {
    clearTimeout(hoverCloseTimer.current);
    placeMenu();
    setIntegrationsOpen(true);
  };

  // Leaving the trigger has to tolerate the cursor crossing the strip of nav between
  // the trigger and the panel, so closing is deferred just long enough to cross it.
  const closeIntegrationsSoon = () => {
    clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = setTimeout(() => setIntegrationsOpen(false), 140);
  };

  useEffect(() => () => clearTimeout(hoverCloseTimer.current), []);

  // Keep the panel under its trigger when the layout moves. The nav is fixed, so only
  // a resize can shift it.
  useEffect(() => {
    if (!integrationsOpen) return;
    window.addEventListener('resize', placeMenu);
    return () => window.removeEventListener('resize', placeMenu);
  }, [integrationsOpen]);

  // Close the integrations dropdown on outside click and on Escape.
  useEffect(() => {
    if (!integrationsOpen) return;
    const onClick = (e) => {
      const inTrigger = integrationsRef.current?.contains(e.target);
      const inMenu = integrationsMenuRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) setIntegrationsOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setIntegrationsOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [integrationsOpen]);

  // Any route change closes both menus, otherwise they survive navigation.
  useEffect(() => {
    setIntegrationsOpen(false);
    setMobileMenuOpen(false);
    document.body.style.overflow = 'auto';
  }, [location.pathname]);

  const closeMobile = () => {
    setMobileMenuOpen(false);
    document.body.style.overflow = 'auto';
  };

  const linkBase = 'transition-colors text-base';
  const linkIdle = d ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900';
  const linkActive = d ? 'text-white font-medium' : 'text-gray-900 font-medium';

  return (
    <>
      <nav
        ref={navRef}
        className={`fixed left-0 right-0 z-50 backdrop-blur-2xl mx-auto max-w-7xl w-[calc(100%-2rem)] rounded-full top-4 border transition-colors duration-300 ${
          d ? 'bg-black/30 border-white/[0.08]' : 'bg-white/70 border-gray-200 shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-10">
              <Link to="/" className="flex items-center" aria-label="PaperBrainz home">
                <img
                  src={d ? paperBrainzLogo : paperBrainzWhiteLogo}
                  alt="PaperBrainz - AI Customer Support Chatbot"
                  className="h-14"
                />
              </Link>

              <div className="hidden md:flex items-center space-x-8">
                {sectionItems.slice(0, 2).map((item) => (
                  <a
                    key={item.id}
                    href={sectionHref(item.id)}
                    className={`${linkBase} ${activeSection === item.id ? linkActive : linkIdle}`}
                  >
                    {item.label}
                  </a>
                ))}

                {/* Integrations — real pages, so a menu rather than an anchor.
                    The panel itself is rendered after </nav>, not here: a backdrop-filter
                    establishes a backdrop root, so a nested backdrop-blur inside the nav
                    has nothing behind it left to sample and renders as flat translucency.
                    Only outside the nav does the glass actually blur the page. */}
                <div
                  onMouseEnter={openIntegrations}
                  onMouseLeave={closeIntegrationsSoon}
                  onFocus={openIntegrations}
                >
                  <button
                    ref={integrationsRef}
                    onClick={() => (integrationsOpen ? setIntegrationsOpen(false) : openIntegrations())}
                    className={`${linkBase} flex items-center gap-1.5 ${
                      PLATFORM_PAGES.some((p) => isCurrent(p.path)) ? linkActive : linkIdle
                    }`}
                    aria-expanded={integrationsOpen}
                    aria-haspopup="true"
                  >
                    {t('nav.integrations')}
                    <ChevronDown className={`w-4 h-4 transition-transform ${integrationsOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                <Link to="/pricing" className={`${linkBase} ${isCurrent('/pricing') ? linkActive : linkIdle}`}>
                  {t('nav.pricing')}
                </Link>

                <a
                  href={sectionHref('faq')}
                  className={`${linkBase} ${activeSection === 'faq' ? linkActive : linkIdle}`}
                >
                  {t('nav.faq')}
                </a>
              </div>
            </div>

            <div className="hidden md:flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className={`p-2.5 rounded-full transition-colors ${d ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-600'}`}
                aria-label="Toggle theme"
              >
                {d ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              {isLoggedIn ? (
                <Link
                  to="/dashboard"
                  className={`px-6 py-2.5 rounded-full transition-all font-medium text-sm ${
                    d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'
                  }`}
                >
                  {t('nav.dashboard')}
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`transition-colors text-sm ${d ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                  >
                    {t('nav.login')}
                  </Link>
                  <Link
                    to="/register"
                    className={`px-5 py-2 rounded-full transition-colors font-medium text-sm ${
                      d ? 'bg-white text-black hover:bg-gray-200' : 'bg-gray-900 text-white hover:bg-black'
                    }`}
                  >
                    {t('nav.startFreeTrial')}
                  </Link>
                </>
              )}
            </div>

            <div className="md:hidden ml-auto flex items-center space-x-2">
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors ${d ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-600'}`}
                aria-label="Toggle theme"
              >
                {d ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={() => {
                  const next = !mobileMenuOpen;
                  setMobileMenuOpen(next);
                  document.body.style.overflow = next ? 'hidden' : 'auto';
                }}
                className="p-2 flex flex-col justify-center items-center w-10 h-10 gap-1.5"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
              >
                <span className={`block w-6 h-0.5 transition-all duration-300 origin-center rounded-full ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''} ${d ? 'bg-[#dbdbdb]' : 'bg-gray-800'}`} />
                <span className={`block w-6 h-0.5 transition-all duration-300 rounded-full ${mobileMenuOpen ? 'opacity-0 scale-0' : ''} ${d ? 'bg-[#dbdbdb]' : 'bg-gray-800'}`} />
                <span className={`block w-6 h-0.5 transition-all duration-300 origin-center rounded-full ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''} ${d ? 'bg-[#dbdbdb]' : 'bg-gray-800'}`} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Integrations panel — see the note at the trigger for why it lives out here.
          Detached from the pill by a gap rather than hanging off it, and carrying the
          nav's own glass values so the two read as the same material. */}
      {integrationsOpen && (
        <div
          ref={integrationsMenuRef}
          className="hidden md:block fixed z-50"
          style={{ left: menuPos.left, top: menuPos.top }}
          onMouseEnter={openIntegrations}
          onMouseLeave={closeIntegrationsSoon}
        >
          {/* Transparent bridge: keeps the pointer "inside" the menu while it crosses
              the gap between the pill and the panel. */}
          <div className="pt-3">
            <div
              className={`w-60 rounded-[18px] border p-2 backdrop-blur-2xl transition-colors duration-300 ${
                d ? 'bg-black/30 border-white/[0.08]' : 'bg-white/70 border-gray-200 shadow-sm'
              }`}
            >
              {PLATFORM_PAGES.map((p) => (
                <Link
                  key={p.slug}
                  to={p.path}
                  className={`block px-3.5 py-2.5 rounded-[12px] text-sm transition-colors ${
                    isCurrent(p.path)
                      ? d
                        ? 'bg-white/[0.06] text-white'
                        : 'bg-gray-100 text-gray-900'
                      : d
                        ? 'text-gray-300 hover:bg-white/[0.06] hover:text-white'
                        : 'text-gray-600 hover:bg-gray-900/[0.04] hover:text-gray-900'
                  }`}
                >
                  {t('nav.chatbotFor')} {p.platform}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile menu */}
      <div
        className={`md:hidden fixed left-4 right-4 top-28 z-40 backdrop-blur-2xl border rounded-[25px] overflow-y-auto transition-all duration-300 ease-out ${
          mobileMenuOpen ? 'max-h-[75vh] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
        } ${d ? 'bg-black/40 border-white/[0.08]' : 'bg-white/80 border-gray-200 shadow-lg'}`}
      >
        <div className="py-6 px-6 space-y-1">
          {sectionItems.slice(0, 2).map((item) => (
            <a
              key={item.id}
              href={sectionHref(item.id)}
              onClick={closeMobile}
              className={`block py-2.5 transition-colors ${d ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
            >
              {item.label}
            </a>
          ))}

          <Link
            to="/pricing"
            onClick={closeMobile}
            className={`block py-2.5 transition-colors ${d ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
          >
            {t('nav.pricing')}
          </Link>

          <a
            href={sectionHref('faq')}
            onClick={closeMobile}
            className={`block py-2.5 transition-colors ${d ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
          >
            {t('nav.faq')}
          </a>

          <div className={`pt-3 mt-2 border-t ${d ? 'border-white/10' : 'border-gray-200'}`}>
            <p className={`text-xs uppercase tracking-wider mb-2 ${d ? 'text-gray-600' : 'text-gray-400'}`}>
              {t('nav.integrations')}
            </p>
            {PLATFORM_PAGES.map((p) => (
              <Link
                key={p.slug}
                to={p.path}
                onClick={closeMobile}
                className={`block py-2.5 text-sm transition-colors ${d ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {t('nav.chatbotFor')} {p.platform}
              </Link>
            ))}
          </div>

          <div className={`pt-4 mt-2 border-t space-y-3 ${d ? 'border-white/10' : 'border-gray-200'}`}>
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                onClick={closeMobile}
                className={`block px-6 py-3 rounded-xl transition-colors font-medium text-center ${
                  d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'
                }`}
              >
                {t('nav.dashboard')}
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={closeMobile}
                  className={`block transition-colors py-2 ${d ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  onClick={closeMobile}
                  className={`block px-6 py-3 rounded-xl transition-colors font-medium text-center ${
                    d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'
                  }`}
                >
                  {t('nav.startFreeTrialArrow')}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default LandingNav;
